import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, CheckCircle2, Loader2, AlertCircle, ClipboardList, ArrowRight, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Reveal } from "@/components/common/Reveal";
import EmptyState from "@/components/common/EmptyState";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import { useInquiryCart } from "@/context/InquiryCartContext";
import { enquiriesService } from "@/services/enquiriesService";
import { brandName } from "@/lib/labels";
import { useFormGuard } from "@/components/common/FormGuard";
import { useSite } from "@/context/SiteDataContext";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[+\d][\d\s()-]{6,}$/;

function Field({ label, required, error, children, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-ink mb-1.5">{label}{required && <span className="text-aqua"> *</span>}</label>
      {children}
      {error && <p data-testid={`${testId}-error`} className="mt-1.5 text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {error}</p>}
    </div>
  );
}

export default function Enquiry() {
  const SITE = useSite();
  const { items, count, setQuantity, setNotes, removeItem, clear } = useInquiryCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [reference, setReference] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const { withGuard, field: honeypot } = useFormGuard();

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const inputCls = (k) => `w-full px-4 py-3 border rounded-md outline-none transition-all focus:ring-2 focus:ring-aqua/20 ${errors[k] ? "border-destructive" : "border-border focus:border-aqua"}`;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!emailRe.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.phone.trim()) e.phone = "Please enter your WhatsApp / mobile number.";
    else if (!phoneRe.test(form.phone)) e.phone = "Please enter a valid phone number.";
    if (!form.company.trim()) e.company = "Please enter your company or organisation.";
    if (!form.message.trim()) e.message = "Please tell us how we can help.";
    if (!agree) e.agree = "Please confirm you agree to be contacted.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await enquiriesService.submitInquiry(withGuard({ ...form, agree, items }));
      setReference(res.reference);
      setStatus("success");
      // Only clear the cart once the enquiry is safely recorded — a failed
      // submission must not lose the customer's product selection.
      clear();
    } catch (error) {
      setErrorMessage(error?.message || "");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <>
        <Seo title="Enquiry Received" />
        <div className="container-x py-24 md:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-xl mx-auto text-center" data-testid="enquiry-success">
            <div className="w-20 h-20 rounded-full bg-aqua/10 flex items-center justify-center mx-auto"><CheckCircle2 className="w-10 h-10 text-aqua" /></div>
            <h1 className="mt-8 text-3xl md:text-4xl font-bold tracking-tight text-ink">Thank you for your enquiry.</h1>
            <p className="mt-4 text-lg text-greyish">Our team will be in touch with you shortly. We appreciate your interest in Athena Scientific.</p>
            {reference && <p className="mt-3 text-sm text-greyish">Reference: <span className="font-semibold text-ink">{reference}</span></p>}
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Link to="/products" data-testid="success-back-products" className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 rounded-md font-semibold hover:bg-black transition-all"><ArrowLeft className="w-4 h-4" /> Back to Products</Link>
              <WhatsAppButton variant="solid" testId="success-whatsapp" />
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo routeKey="enquiry" title="Send an Enquiry" description="Tell us how we can help. Your selected products are included automatically." canonical={`https://${SITE.website}/enquiry`} />
      <div className="container-x pt-8"><Breadcrumbs crumbs={[{ label: "Inquiry Cart", to: "/cart" }, { label: "Enquiry" }]} /></div>

      <div className="container-x py-10 md:py-14">
        <Reveal>
          <p className="eyebrow">{SITE.keyQuestion}</p>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-ink">Tell Us How We Can Help</h1>
          <p className="mt-3 text-greyish max-w-2xl">Share a few details and your selected products below. Our team will follow up with recommendations and documentation.</p>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 order-2 lg:order-1" data-testid="enquiry-form" noValidate>
            {honeypot}
            {status === "error" && (
              <div className="mb-6 flex items-center gap-2 p-4 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="enquiry-error"><AlertCircle className="w-5 h-5 shrink-0" /> {errorMessage || "Something went wrong submitting your enquiry. Please try again."}</div>
            )}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name" required error={errors.name} testId="name"><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="enquiry-name" className={inputCls("name")} placeholder="Your full name" /></Field>
              <Field label="Email" required error={errors.email} testId="email"><input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="enquiry-email" className={inputCls("email")} placeholder="you@company.com" /></Field>
              <Field label="WhatsApp / Mobile Number" required error={errors.phone} testId="phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="enquiry-phone" className={inputCls("phone")} placeholder="+65 …" /></Field>
              <Field label="Company" required error={errors.company} testId="company"><input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="enquiry-company" className={inputCls("company")} placeholder="Your organisation" /></Field>
            </div>
            <div className="mt-5">
              <Field label="Message" required error={errors.message} testId="message"><textarea value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="enquiry-message" rows={5} className={`${inputCls("message")} resize-none`} placeholder="Tell us about your application, objectives or questions…" /></Field>
            </div>
            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => { setAgree(e.target.checked); setErrors((er) => ({ ...er, agree: undefined })); }} data-testid="enquiry-agree" className="mt-0.5 w-5 h-5 rounded border-border accent-[#52CDD7]" />
              <span className="text-sm text-greyish">I agree to be contacted regarding this enquiry.</span>
            </label>
            {errors.agree && <p data-testid="agree-error" className="mt-1.5 text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {errors.agree}</p>}

            <button type="submit" disabled={status === "submitting"} data-testid="enquiry-submit" className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-aqua text-white px-8 py-4 rounded-md font-semibold hover:bg-aqua-dark transition-all disabled:opacity-70">
              {status === "submitting" ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : <>Submit Product Enquiry <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {/* Selected products */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="rounded-lg border border-border bg-[#f9fafb] p-6">
              <div className="flex items-center justify-between">
                <p className="font-bold text-ink flex items-center gap-2"><ClipboardList className="w-5 h-5 text-aqua" /> Selected Products</p>
                <span className="text-sm text-greyish">{count} item{count !== 1 ? "s" : ""}</span>
              </div>

              {items.length === 0 ? (
                <div className="mt-2"><EmptyState icon={ClipboardList} title="No products selected" description="Add products to include them in your enquiry." actionLabel="Browse Products" actionTo="/products" testId="enquiry-empty" /></div>
              ) : (
                <div className="mt-5 space-y-4" data-lenis-prevent>
                  {items.map((it) => (
                    <div key={it.productId} data-testid={`enquiry-item-${it.slug}`} className="flex gap-3 pb-4 border-b border-border last:border-0 last:pb-0">
                      <img src={it.image} alt={it.name} loading="lazy" decoding="async" className="w-14 h-14 rounded-md object-cover bg-white shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[0.62rem] uppercase tracking-widest font-semibold text-greyish">{brandName(it.brand)}</p>
                        <p className="text-sm font-semibold text-ink truncate">{it.name}</p>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="inline-flex items-center border border-border rounded-md bg-white">
                            <button type="button" onClick={() => setQuantity(it.productId, it.quantity - 1)} data-testid={`enquiry-dec-${it.slug}`} className="w-7 h-7 flex items-center justify-center text-greyish hover:text-ink"><Minus className="w-3 h-3" /></button>
                            <span className="w-7 text-center text-sm font-semibold">{it.quantity}</span>
                            <button type="button" onClick={() => setQuantity(it.productId, it.quantity + 1)} data-testid={`enquiry-inc-${it.slug}`} className="w-7 h-7 flex items-center justify-center text-greyish hover:text-ink"><Plus className="w-3 h-3" /></button>
                          </div>
                          <button type="button" onClick={() => removeItem(it.productId)} data-testid={`enquiry-remove-${it.slug}`} className="text-greyish hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <textarea value={it.notes} onChange={(e) => setNotes(it.productId, e.target.value)} data-testid={`enquiry-notes-${it.slug}`} rows={1} placeholder="Note (optional)…" className="mt-2 w-full px-3 py-2 text-xs border border-border rounded-md outline-none focus:border-aqua bg-white resize-none" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
